import React, { useState } from 'react';
import {
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  alpha,
  useTheme,
} from '@mui/material';
import {
  MoreVert,
  OpenInNew,
  GetApp,
  Email,
  Visibility,
  History,
} from '@mui/icons-material';
import { ActionModal } from './ActionModal';
import { MonitoredAssuntoEyeIcon } from './MonitoredAssuntoEyeIcon';
import { GreeningTableBadge } from './greening/GreeningTableBadge';

interface ActionCellProps {
  item: any;
  monitoringStatus?: { [key: string | number]: boolean };
  onViewDetails?: (item: any) => void;
  onViewHistory?: (item: any) => void;
  onDownload?: (item: any) => void;
  onSendEmail?: (emailData: { to: string; subject: string; item: any }) => Promise<void>;
  showMonitoringBadge?: boolean;
  /** Badges de monitoramento (norma, assunto, palavra-chave, agência) vindos da API. */
  monitoringBadges?: React.ReactNode;
  isAssuntoMonitored?: boolean;
  assuntoMonitoringLabel?: string | null;
  type?: 'legislation' | 'change';
  forceShowMonitoring?: boolean;
  monitoringHistory?: any[];
  isMonitored?: boolean;
  onToggleMonitoring?: (itemId: number) => void;
  // Propriedades para controle do modal unificado
  showActions?: boolean;
  /** Alinhamento horizontal dos ícones na célula */
  contentAlign?: 'center' | 'end';
  modalProps?: {
    title?: string;
    showEmail?: boolean;
    showDownload?: boolean;
    showHistory?: boolean;
    showMonitoring?: boolean;
  };
}

export function ActionCell({
  item,
  monitoringStatus,
  onViewDetails,
  onViewHistory,
  onDownload,
  onSendEmail,
  showMonitoringBadge = false,
  monitoringBadges,
  isAssuntoMonitored = false,
  assuntoMonitoringLabel,
  type = 'legislation',
  forceShowMonitoring = false,
  monitoringHistory = [],
  isMonitored = false,
  onToggleMonitoring,
  showActions = true,
  contentAlign = 'end',
  modalProps = {},
}: ActionCellProps) {
  const theme = useTheme();
  const [actionMenuAnchor, setActionMenuAnchor] = useState<HTMLElement | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);

  // Early return if item is not provided
  if (!item) {
    return null;
  }

  const handleActionMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setActionMenuAnchor(event.currentTarget);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
  };

  const handleDetailsModalClose = () => {
    setDetailsModalOpen(false);
  };

  // Handlers para ações específicas
  const handleViewDetailsClick = () => {
    if (onViewDetails) {
      onViewDetails(item);
    } else {
      setDetailsModalOpen(true);
    }
    handleActionMenuClose();
  };

  const handleViewHistoryClick = () => {
    if (onViewHistory) {
      onViewHistory(item);
    }
    handleActionMenuClose();
  };

  const handleDownloadClick = () => {
    if (onDownload) {
      onDownload(item);
    }
    handleActionMenuClose();
  };

  const handleSendEmailClick = () => {
    if (onSendEmail) {
      onSendEmail({ to: '', subject: '', item });
    }
    handleActionMenuClose();
  };

  // Verificar se está sendo monitorado de forma robusta
  const itemId = item?.id || item?._id;
  const itemIsMonitored = forceShowMonitoring || (itemId && monitoringStatus && (
    monitoringStatus[itemId] === true ||
    monitoringStatus[Number(itemId)] === true ||
    monitoringStatus[String(itemId)] === true
  ));

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        justifyContent={contentAlign === 'center' ? 'center' : 'flex-end'}
        gap={0.5}
        flexWrap="wrap"
        sx={{ minWidth: 0, width: contentAlign === 'center' ? '100%' : undefined }}
      >
        {type === 'change' && (
          <GreeningTableBadge
            status={item.greening_status}
            questionsCount={item.greening_questions_count}
          />
        )}
        {monitoringBadges ?? (
          <>
            {isAssuntoMonitored && (
              <MonitoredAssuntoEyeIcon
                assuntoLabel={assuntoMonitoringLabel}
                stopPropagation
              />
            )}
            {showMonitoringBadge && itemIsMonitored && !isAssuntoMonitored && (
              <Tooltip title="Monitorado" arrow>
                <Chip
                  icon={<Visibility sx={{ fontSize: 16 }} />}
                  label="Ativo"
                  size="small"
                  color="success"
                  variant="filled"
                  sx={{ height: 24, fontSize: '0.75rem' }}
                />
              </Tooltip>
            )}
          </>
        )}
        <IconButton size="small" onClick={handleActionMenuOpen}>
          <MoreVert fontSize="small" />
        </IconButton>
      </Box>

      {/* Menu de Ações */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionMenuClose}
      >
        {modalProps?.showHistory !== false && onViewHistory && (
          <MenuItem onClick={handleViewHistoryClick}>
            <History sx={{ mr: 1, fontSize: 20 }} />
            Ver {type === 'legislation' ? 'Detalhes Completos' : 'Detalhes'}
          </MenuItem>
        )}
        {modalProps?.showEmail !== false && onViewDetails && (
          <MenuItem onClick={handleViewDetailsClick}>
            <OpenInNew sx={{ mr: 1, fontSize: 20 }} />
            Ver Detalhes
          </MenuItem>
        )}
        {modalProps?.showDownload !== false && onDownload && (
          <MenuItem onClick={handleDownloadClick}>
            <GetApp sx={{ mr: 1, fontSize: 20 }} />
            Baixar Documento
          </MenuItem>
        )}
        {modalProps?.showEmail !== false && onSendEmail && (
          <MenuItem onClick={handleSendEmailClick}>
            <Email sx={{ mr: 1, fontSize: 20 }} />
            Enviar por E-mail
          </MenuItem>
        )}
      </Menu>

      {/* Modal de Detalhes Unificado */}
      <ActionModal
        open={detailsModalOpen}
        onClose={handleDetailsModalClose}
        item={item}
        type={type}
        monitoringHistory={monitoringHistory}
        isMonitored={itemIsMonitored}
        onToggleMonitoring={onToggleMonitoring}
        onSendEmail={onSendEmail}
        modalProps={modalProps}
      />
    </>
  );
}
